import * as THREE from "three";

import { memo, useEffect, useRef } from "react";
import { useThree } from "@react-three/fiber";
import { CameraControls } from "@react-three/drei";
import { useRoute } from "wouter";

import { CANVAS_ANIMATION_RATE } from "../../utils/animationTiming";

export const Rig = memo(function Rig({ disableInteraction = false }) {
  const { controls, scene } = useThree();
  const [, params] = useRoute("/neo/:id");
  const portalId = params?.id ?? null;
  const positionRef = useRef(new THREE.Vector3(0, 0, 2));
  const focusRef = useRef(new THREE.Vector3(0, 0, 0));

  useEffect(() => {
    if (!controls) return;

    const position = positionRef.current;
    const focus = focusRef.current;
    controls.minDistance = 1.5;
    controls.maxDistance = 5;
    controls.enableRotate = true;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.enableDamping = false;
    controls.maxPolarAngle = Math.PI / 2;

    const active = portalId ? scene.getObjectByName(portalId) : null;

    if (active?.parent) {
      const cameraOffsetZ = portalId === "meet-joz"
        ? 0.08
        : 0.291;
      active.parent.localToWorld(position.set(0, 0, cameraOffsetZ));
      active.parent.localToWorld(focus.set(0, 0, -2));
    } else {
      position.set(0, 0, 2);
      focus.set(0, 0, 0);
    }

    // Keep the root return animated, but discard any velocity from the portal
    // camera before retargeting. This prevents the last few damped frames from
    // surfacing as a small vertical nudge when the Joz MAXX button is clicked.
    if (!portalId) controls.stop();
    controls.setLookAt(...position.toArray(), ...focus.toArray(), true);
  }, [controls, portalId, scene]);

  useEffect(() => {
    if (!controls) return;
    controls.enabled = !disableInteraction;
    return () => {
      controls.enabled = true;
    };
  }, [controls, disableInteraction]);

  useEffect(() => {
    if (!controls || typeof window === "undefined") return undefined;

    const handleCameraCommand = (event) => {
      const direction = String(event.detail?.direction || "").toLowerCase().trim();
      if (!["right", "left", "up", "down"].includes(direction)) return;

      const degrees = THREE.MathUtils.clamp(
        Number(event.detail?.degrees) || 42,
        8,
        90
      );
      const radians = THREE.MathUtils.degToRad(degrees);
      const azimuth = direction === "right" ? -radians : direction === "left" ? radians : 0;
      const polar = direction === "down" ? -radians : direction === "up" ? radians : 0;

      controls.rotate(azimuth, polar, true);
    };

    window.addEventListener("world-camera-command", handleCameraCommand);
    return () => window.removeEventListener("world-camera-command", handleCameraCommand);
  }, [controls]);

  return (
    <CameraControls
      makeDefault
      minPolarAngle={0}
      maxPolarAngle={Math.PI / 2}
      // Balanced pointer blending: visible glide without the old input lag.
      draggingSmoothTime={0.1 / CANVAS_ANIMATION_RATE}
      smoothTime={0.2 / CANVAS_ANIMATION_RATE}
    />
  );
});
